// Protected Route Component for authentication-required pages
// Implements route guards based on PRD authentication requirements

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEntitlements } from '../../hooks/useEntitlements';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailVerified?: boolean;
  requireProfileComplete?: boolean;
  requiredEntitlement?: string;
  fallbackPath?: string;
}

export function ProtectedRoute({
  children,
  requireEmailVerified = false,
  requireProfileComplete = false,
  requiredEntitlement,
  fallbackPath = '/',
}: ProtectedRouteProps) {
  const { user, isLoading, isEmailVerified, profileCompleted } = useAuth();
  const { hasEntitlement, isLoading: entitlementsLoading } = useEntitlements();
  const location = useLocation();

  // Show loading state while authentication or entitlements are being determined
  if (isLoading || (requiredEntitlement && entitlementsLoading)) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!user) {
    return (
      <Navigate
        to="/"
        state={{ from: location.pathname, returnTo: location }}
        replace
      />
    );
  }

  // Check email verification requirement (PRD Section 5.2)
  if (requireEmailVerified && !isEmailVerified) {
    return (
      <Navigate
        to="/verify-email"
        state={{ from: location.pathname, returnTo: location }}
        replace
      />
    );
  }

  // Check profile completion requirement (PRD Section 5.2)
  if (requireProfileComplete && !profileCompleted) {
    return (
      <Navigate
        to="/complete-profile"
        state={{ from: location.pathname, returnTo: location }}
        replace
      />
    );
  }

  // Check entitlement-based access from ID token claims
  if (requiredEntitlement && !hasEntitlement(requiredEntitlement)) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ error: 'Access denied: missing entitlement' }}
        replace
      />
    );
  }

  // All checks passed, render protected content
  return children;
}

// Convenience wrapper for admin-only routes
export function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute
      requireEmailVerified={true}
      requireProfileComplete={true}
      requiredEntitlement="sys:admin"
      fallbackPath="/dashboard"
    >
      {children}
    </ProtectedRoute>
  );
}

// Convenience wrapper for verified users only
export function VerifiedRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requireEmailVerified={true} requireProfileComplete={true}>
      {children}
    </ProtectedRoute>
  );
}
